import React, { useState, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { Edit2, Send, Trash2, Plus, Search, Download,
         CheckCircle, Clock, AlertCircle, Minus, UserCheck, X, Users,
         ChevronDown, Calendar } from 'lucide-react';
import api from '../../api/axios';
import { useToast } from '../Toast';

/* ─── Status config ───────────────────────────────────────── */
const STATUS = {
  a_jour:    { label: 'À jour',     dot: 'bg-emerald-500', badge: 'bg-emerald-50 text-emerald-700', bar: 'bg-emerald-500', icon: CheckCircle },
  bientot:   { label: 'Bientôt',   dot: 'bg-amber-400',   badge: 'bg-amber-50  text-amber-700',   bar: 'bg-amber-400',   icon: Clock        },
  en_retard: { label: 'En retard', dot: 'bg-red-500',     badge: 'bg-red-50    text-red-700',     bar: 'bg-red-500',     icon: AlertCircle  },
  non_suivi: { label: 'Non suivi', dot: 'bg-slate-300',   badge: 'bg-slate-100 text-slate-400',   bar: 'bg-slate-200',   icon: Minus },
};

const FILTERS = [
  { key: 'all',       label: 'Tous'       },
  { key: 'en_retard', label: 'En retard'  },
  { key: 'bientot',   label: 'Bientôt'    },
  { key: 'a_jour',    label: 'À jour'     },
  { key: 'non_suivi', label: 'Non suivis' },
];

const STATUS_COLORS = {
  'Confirmé':  { bg: '#ecfdf5', color: '#059669' },
  'Terminé':   { bg: '#eff6ff', color: '#2563eb' },
  'Annulé':    { bg: '#fef2f2', color: '#dc2626' },
  'En attente':{ bg: '#fffbeb', color: '#d97706' },
};

/* ─── Helpers ─────────────────────────────────────────────── */
function fmt(d) {
  if (!d) return null;
  return new Date(d).toLocaleDateString('fr-FR', { day: 'numeric', month: 'short', year: 'numeric' });
}
function fmtShort(d) {
  if (!d) return null;
  return new Date(d).toLocaleDateString('fr-FR', { day: 'numeric', month: 'short' });
}
function daysUntil(d) {
  if (!d) return null;
  return Math.ceil((new Date(d) - new Date()) / 86400000);
}

/* ─── Visit popover ───────────────────────────────────────── */
function VisitPopover({ anchorRect, onConfirm, onClose }) {
  const ref   = useRef(null);
  const today = new Date().toISOString().slice(0, 10);
  const [date, setDate] = useState(today);

  useEffect(() => {
    const h = (e) => { if (ref.current && !ref.current.contains(e.target)) onClose(); };
    document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, [onClose]);

  const style = {
    position: 'fixed',
    top:  (anchorRect?.bottom ?? 0) + 8,
    left: Math.min((anchorRect?.right ?? 0) - 224, window.innerWidth - 240),
    zIndex: 9999,
    width: 224,
  };

  return createPortal(
    <div ref={ref} style={style}
      className="bg-white rounded-2xl shadow-xl border border-slate-200 p-4">
      <div className="flex items-center justify-between mb-2.5">
        <p className="text-xs font-bold text-slate-500 uppercase tracking-wide">Date de visite</p>
        <button onClick={onClose} className="text-slate-400 hover:text-slate-600"><X size={12}/></button>
      </div>
      <input type="date" value={date} max={today}
        onChange={e => setDate(e.target.value)}
        className="w-full rounded-xl border border-slate-200 bg-slate-50 px-3 py-2 text-sm text-slate-900 focus:outline-none focus:ring-2 focus:ring-emerald-400 mb-3" />
      <button onClick={() => onConfirm(date)}
        className="w-full py-2 bg-emerald-600 text-white text-xs font-bold rounded-xl hover:bg-emerald-700 transition">
        Enregistrer
      </button>
    </div>,
    document.body
  );
}

/* ─── Status badge ────────────────────────────────────────── */
function StatusBadge({ status }) {
  const cfg  = STATUS[status] || STATUS.non_suivi;
  const Icon = cfg.icon;
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold ${cfg.badge}`}>
      <Icon size={10} strokeWidth={2.5} />
      {cfg.label}
    </span>
  );
}

/* ─── Next visit display ──────────────────────────────────── */
function NextVisit({ date, status }) {
  const d = daysUntil(date);
  if (d === null) return <span className="text-slate-300">-</span>;

  if (status === 'en_retard') return (
    <span className="text-red-600 font-semibold text-xs">
      {Math.abs(d)}j de retard
    </span>
  );
  if (status === 'bientot') return (
    <span className="text-amber-600 font-semibold text-xs">
      Dans {d}j · {fmt(date)}
    </span>
  );
  return <span className="text-slate-500 text-xs">{fmt(date)}</span>;
}

/* ─── Client history panel ────────────────────────────────── */
function ClientHistoryPanel({ client, currency }) {
  const [history, setHistory] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get(`/clients/${client._id}/history`)
      .then(r => setHistory(r.data.data))
      .catch(() => setHistory({ bookings: [], totalCA: 0, totalTransactions: 0, totalVisits: 0 }))
      .finally(() => setLoading(false));
  }, [client._id]);

  if (loading) return (
    <div className="px-6 py-5 flex items-center gap-3">
      <div className="w-4 h-4 rounded-full border-2 border-indigo-400 border-t-transparent animate-spin" />
      <span className="text-xs text-slate-400 font-medium">Chargement du profil...</span>
    </div>
  );

  if (!history) return null;

  const visits = history.totalVisits || 0;
  const ca     = history.totalCA || 0;
  const bks    = history.bookings || [];

  return (
    <div className="border-t border-slate-100 bg-slate-50/60">
      {/* Stats row */}
      <div className="grid grid-cols-3 divide-x divide-slate-200 px-4 py-4">
        <div className="flex flex-col items-center gap-0.5 px-3">
          <span className="text-[10px] font-bold uppercase tracking-widest text-slate-400">Visites</span>
          <span className="text-xl font-black text-slate-800 leading-none">{visits}</span>
        </div>
        <div className="flex flex-col items-center gap-0.5 px-3">
          <span className="text-[10px] font-bold uppercase tracking-widest text-slate-400">CA encaissé</span>
          <span className="text-xl font-black text-slate-800 leading-none">
            {ca > 0 ? ca.toLocaleString() : '-'}
          </span>
          {ca > 0 && <span className="text-[10px] text-slate-400 font-bold">{currency || ''}</span>}
        </div>
        <div className="flex flex-col items-center gap-0.5 px-3">
          <span className="text-[10px] font-bold uppercase tracking-widest text-slate-400">Dernière visite</span>
          <span className="text-sm font-bold text-slate-700 text-center leading-tight">
            {history.lastVisitDate ? fmt(history.lastVisitDate) : '-'}
          </span>
        </div>
      </div>

      {/* Bookings list */}
      {bks.length > 0 ? (
        <div className="px-4 pb-4 space-y-1.5">
          <p className="text-[10px] font-bold uppercase tracking-widest text-slate-400 mb-2">Derniers rendez-vous</p>
          {bks.map((b, i) => {
            const sc = STATUS_COLORS[b.statut] || { bg: '#f1f5f9', color: '#64748b' };
            return (
              <div key={b._id || i}
                className="flex items-center gap-3 bg-white rounded-xl px-3 py-2.5 border border-slate-100">
                <div className="w-1.5 h-1.5 rounded-full flex-shrink-0" style={{ background: sc.color }} />
                <span className="flex-1 text-sm font-semibold text-slate-700 truncate">
                  {b.service || 'Prestation'}
                </span>
                <span className="text-xs text-slate-400 font-medium flex-shrink-0">{fmtShort(b.date)}</span>
                <span className="text-[10px] font-bold px-2 py-0.5 rounded-full flex-shrink-0"
                  style={{ background: sc.bg, color: sc.color }}>
                  {b.statut || 'RDV'}
                </span>
              </div>
            );
          })}
        </div>
      ) : (
        <div className="px-4 pb-4 flex items-center gap-2 text-slate-400 text-xs">
          <Calendar size={13} />
          Aucun rendez-vous trouvé pour ce client
        </div>
      )}
    </div>
  );
}

/* ─── Main component ──────────────────────────────────────── */
export default function ClientsTable({ clients, loading, onEdit, onRefresh, onNew, currency }) {
  const showToast = useToast();
  const [search,    setSearch]    = useState('');
  const [filter,    setFilter]    = useState('all');
  const [sending,   setSending]   = useState(null);
  const [importing, setImporting] = useState(false);
  const [visitPop,  setVisitPop]  = useState(null);
  const [expanded,  setExpanded]  = useState(null);

  const handleMarkVisit = async (id, date) => {
    setVisitPop(null);
    try {
      await api.put(`/clients/${id}`, { lastVisitDate: date });
      showToast('Visite enregistrée', 'success');
      onRefresh();
    } catch { showToast('Erreur mise à jour', 'error'); }
  };

  const handleSend = async (id, hasEmail) => {
    if (!hasEmail) return showToast('Pas d\'email renseigné pour ce client', 'warning');
    setSending(id);
    try {
      await api.post(`/rappels/send/${id}`);
      showToast('Rappel envoyé', 'success');
      onRefresh();
    } catch (err) { showToast(err.response?.data?.message || 'Erreur envoi', 'error'); }
    finally { setSending(null); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Supprimer ce client ?')) return;
    try { await api.delete(`/clients/${id}`); showToast('Client supprimé', 'success'); onRefresh(); }
    catch { showToast('Erreur suppression', 'error'); }
  };

  const handleImport = async () => {
    setImporting(true);
    try {
      const { data } = await api.post('/clients/import');
      showToast(data.created > 0 ? `${data.created} client(s) importé(s)` : 'Aucun nouveau client', 'success');
      onRefresh();
    } catch { showToast('Erreur import', 'error'); }
    finally { setImporting(false); }
  };

  const counts   = clients.reduce((a, c) => { a[c.status] = (a[c.status] || 0) + 1; return a; }, {});
  const filtered = clients.filter(c => {
    const s = search.toLowerCase();
    const matchSearch = !s || [c.nom, c.prenom, c.email, c.telephone].some(v => v?.toLowerCase().includes(s));
    return matchSearch && (filter === 'all' || c.status === filter);
  });

  /* Loading skeleton */
  if (loading) return (
    <div className="space-y-2">
      {[1,2,3,4,5].map(i => (
        <div key={i} className="h-[72px] rounded-2xl bg-white animate-pulse border border-slate-100" />
      ))}
    </div>
  );

  return (
    <div className="space-y-4">

      {/* ── Toolbar ── */}
      <div className="flex flex-wrap gap-2.5">
        <div className="relative flex-1 min-w-52">
          <Search size={14} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" />
          <input
            className="w-full pl-9 pr-4 py-2.5 text-sm rounded-xl border border-slate-200 bg-white text-slate-900 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-indigo-400 focus:border-transparent transition"
            placeholder="Rechercher…"
            value={search} onChange={e => setSearch(e.target.value)} />
        </div>
        <button onClick={handleImport} disabled={importing}
          className="flex items-center gap-2 px-4 py-2.5 text-sm rounded-xl border border-slate-200 bg-white text-slate-600 hover:bg-slate-50 font-medium transition disabled:opacity-50">
          <Download size={14} />
          {importing ? 'Import…' : 'Importer RDV'}
        </button>
        <button onClick={onNew}
          className="flex items-center gap-2 px-4 py-2.5 text-sm rounded-xl bg-indigo-600 text-white font-bold hover:bg-indigo-700 transition">
          <Plus size={14} />
          Nouveau client
        </button>
      </div>

      {/* ── Filter pills ── */}
      <div className="flex gap-1.5 flex-wrap">
        {FILTERS.map(f => {
          const cnt = f.key === 'all' ? clients.length : (counts[f.key] || 0);
          const active = filter === f.key;
          return (
            <button key={f.key} onClick={() => setFilter(f.key)}
              className={`inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold rounded-full border transition-all
                ${active
                  ? 'bg-slate-900 text-white border-slate-900'
                  : 'border-slate-200 text-slate-500 bg-white hover:border-slate-400'}`}>
              {f.label}
              <span className={`text-xs px-1.5 py-px rounded-full font-bold
                ${active ? 'bg-white/20' : 'bg-slate-100 text-slate-500'}`}>
                {cnt}
              </span>
            </button>
          );
        })}
      </div>

      {/* ── List ── */}
      {filtered.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-20 bg-white rounded-2xl border border-dashed border-slate-200">
          <div className="w-14 h-14 rounded-2xl bg-slate-100 flex items-center justify-center mb-4">
            <Users size={24} className="text-slate-400" />
          </div>
          <p className="font-semibold text-slate-600 mb-1">
            {search || filter !== 'all' ? 'Aucun client trouvé' : 'Pas encore de clients'}
          </p>
          <p className="text-sm text-slate-400 mb-5">
            {search || filter !== 'all' ? 'Essayez d\'autres filtres' : 'Importez depuis vos réservations ou créez manuellement'}
          </p>
          {!search && filter === 'all' && (
            <div className="flex gap-2">
              <button onClick={handleImport} disabled={importing}
                className="px-4 py-2 text-sm rounded-xl border border-slate-200 text-slate-600 hover:bg-slate-50 font-medium transition">
                <Download size={13} className="inline mr-1.5" />Importer
              </button>
              <button onClick={onNew}
                className="px-4 py-2 text-sm rounded-xl bg-indigo-600 text-white font-bold hover:bg-indigo-700 transition">
                <Plus size={13} className="inline mr-1.5" />Créer
              </button>
            </div>
          )}
        </div>
      ) : (
        <div className="rounded-2xl border border-slate-200 bg-white overflow-hidden shadow-sm divide-y divide-slate-100">
          {filtered.map(c => {
            const cfg = STATUS[c.status] || STATUS.non_suivi;
            const initial = ((c.prenom || c.nom || '?').charAt(0)).toUpperCase();
            const name = c.prenom ? `${c.prenom} ${c.nom}` : c.nom;
            const isExpanded = expanded === c._id;

            return (
              <div key={c._id} className="group">

                {/* Row */}
                <div className="flex items-center gap-0 hover:bg-slate-50 transition-colors">

                  {/* Status bar */}
                  <div className={`self-stretch w-1 flex-shrink-0 ${cfg.bar}`} />

                  {/* Avatar */}
                  <div className="w-9 h-9 rounded-xl flex items-center justify-center text-sm font-bold text-white flex-shrink-0 ml-4"
                    style={{ background: c.status === 'a_jour' ? '#10b981' : c.status === 'bientot' ? '#f59e0b' : c.status === 'en_retard' ? '#ef4444' : '#cbd5e1' }}>
                    {initial}
                  </div>

                  {/* Info principale */}
                  <button
                    type="button"
                    onClick={() => setExpanded(isExpanded ? null : c._id)}
                    className="flex-1 min-w-0 px-3.5 py-3.5 text-left"
                  >
                    <div className="flex items-center gap-2 flex-wrap">
                      <p className="font-semibold text-slate-900 text-sm leading-none">{name}</p>
                      <StatusBadge status={c.status} />
                      {c.totalVisits > 0 && (
                        <span className="text-[10px] text-slate-400 font-bold hidden sm:inline">
                          {c.totalVisits} visite{c.totalVisits > 1 ? 's' : ''}
                        </span>
                      )}
                      {c.reminderEnabled && (
                        <span className="text-xs text-indigo-400 font-medium hidden sm:inline">Rappels actifs</span>
                      )}
                    </div>
                    <div className="flex items-center gap-3 mt-1.5 flex-wrap">
                      {c.email && (
                        <span className="text-xs text-slate-400 truncate max-w-[180px]">{c.email}</span>
                      )}
                      {c.preferredServiceId?.nom && (
                        <span className="text-xs text-slate-400 hidden sm:inline">· {c.preferredServiceId.nom}</span>
                      )}
                    </div>
                  </button>

                  {/* Dates */}
                  <div className="hidden md:flex flex-col items-end gap-1 px-4 flex-shrink-0">
                    <div className="flex items-center gap-1.5">
                      <span className="text-xs text-slate-400">Dernière visite</span>
                      <span className="text-xs font-medium text-slate-600">
                        {fmt(c.lastVisitDate) || <span className="text-slate-300">-</span>}
                      </span>
                    </div>
                    <div className="flex items-center gap-1.5">
                      <span className="text-xs text-slate-400">Prochain passage</span>
                      <NextVisit date={c.nextVisitTarget} status={c.status} />
                    </div>
                  </div>

                  {/* Actions */}
                  <div className="flex items-center gap-1 pr-2 flex-shrink-0">
                    <button
                      onClick={() => setExpanded(isExpanded ? null : c._id)}
                      title="Voir le profil"
                      className={`p-1.5 rounded-lg transition-colors ${isExpanded ? 'text-indigo-600 bg-indigo-50' : 'text-slate-400 hover:text-indigo-600 hover:bg-indigo-50'}`}>
                      <ChevronDown size={14} className={`transition-transform ${isExpanded ? 'rotate-180' : ''}`} />
                    </button>
                    <button
                      onClick={e => {
                        const rect = e.currentTarget.getBoundingClientRect();
                        setVisitPop(visitPop?.id === c._id ? null : { id: c._id, rect });
                      }}
                      title="Marquer comme venu"
                      className="flex items-center gap-1 px-2.5 py-1.5 rounded-lg bg-emerald-50 text-emerald-700 hover:bg-emerald-100 text-xs font-semibold transition">
                      <UserCheck size={12} />
                      <span className="hidden sm:inline">Venu</span>
                    </button>
                    {visitPop?.id === c._id && (
                      <VisitPopover
                        anchorRect={visitPop.rect}
                        onConfirm={date => handleMarkVisit(c._id, date)}
                        onClose={() => setVisitPop(null)} />
                    )}
                    <button onClick={() => handleSend(c._id, !!c.email)} disabled={sending === c._id}
                      title={c.email ? 'Envoyer un rappel' : 'Aucun email'}
                      className="flex items-center gap-1 px-2.5 py-1.5 rounded-lg bg-indigo-50 text-indigo-600 hover:bg-indigo-100 text-xs font-semibold disabled:opacity-40 transition">
                      <Send size={12} />
                      <span className="hidden sm:inline">{sending === c._id ? '…' : 'Rappel'}</span>
                    </button>
                    <button onClick={() => onEdit(c)} title="Modifier"
                      className="p-1.5 rounded-lg text-slate-400 hover:text-indigo-600 hover:bg-indigo-50 transition">
                      <Edit2 size={14} />
                    </button>
                    <button onClick={() => handleDelete(c._id)} title="Supprimer"
                      className="p-1.5 rounded-lg text-slate-400 hover:text-red-500 hover:bg-red-50 transition">
                      <Trash2 size={14} />
                    </button>
                  </div>
                </div>

                {/* Expandable history panel */}
                {isExpanded && (
                  <ClientHistoryPanel client={c} currency={currency} />
                )}
              </div>
            );
          })}
        </div>
      )}

      <p className="text-xs text-slate-400 text-right">
        {filtered.length} client{filtered.length > 1 ? 's' : ''}
        {filter !== 'all' ? ` · filtre : ${FILTERS.find(f=>f.key===filter)?.label}` : ''}
      </p>
    </div>
  );
}
